/// <reference lib="dom" />
/** Browser-only transport. 100% sent means processing, never saved. */
export type UploadProgress = { phase: 'sending' | 'processing'; loaded: number; total: number };

export function uploadRequest(url: string, init: RequestInit & { body: Blob }, onProgress?: (value: UploadProgress) => void): Promise<Response> {
  if (!onProgress || typeof XMLHttpRequest === 'undefined') return fetch(url, init);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const signal = init.signal;
    const cleanup = () => signal?.removeEventListener('abort', abort);
    const fail = (error: Error) => { cleanup(); reject(error); };
    const abort = () => { xhr.abort(); fail(new DOMException('Upload aborted', 'AbortError')); };
    if (signal?.aborted) { abort(); return; }
    xhr.open(init.method ?? 'POST', url);
    xhr.withCredentials = init.credentials === 'include';
    new Headers(init.headers).forEach((value, key) => xhr.setRequestHeader(key, value));
    xhr.upload.onprogress = (event) => onProgress({
      phase: event.lengthComputable && event.loaded >= event.total ? 'processing' : 'sending',
      loaded: event.loaded, total: event.lengthComputable ? event.total : init.body.size,
    });
    xhr.upload.onload = () => onProgress({ phase: 'processing', loaded: init.body.size, total: init.body.size });
    xhr.onload = () => {
      cleanup();
      if (!xhr.status) { fail(new TypeError('No upload response')); return; }
      const headers = new Headers();
      for (const line of xhr.getAllResponseHeaders().trim().split(/[\r\n]+/)) {
        const at = line.indexOf(':');
        if (at > 0) headers.append(line.slice(0, at), line.slice(at + 1).trim());
      }
      resolve(new Response(xhr.status === 204 ? null : xhr.responseText, { status: xhr.status, headers }));
    };
    xhr.onerror = () => fail(new TypeError('Network connection lost during upload'));
    xhr.onabort = () => fail(new DOMException('Upload aborted', 'AbortError'));
    signal?.addEventListener('abort', abort, { once: true });
    onProgress({ phase: 'sending', loaded: 0, total: init.body.size });
    xhr.send(init.body);
  });
}
